import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { IndentedSections } from '@openx/components-core/lib/IndentedSection/IndentedSections.tsx';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type { LookalikeFormSegment } from '../../../../../types.ts';
import { useReferenceData } from '../../../hooks/useReferenceData.ts';
import { BasicInfo } from '../../drawer/components/SegmentListItem/BasicInfo.tsx';
import { SegmentHeader } from '../../drawer/components/SegmentListItem/SegmentHeader.tsx';
import type {
  ExpandableElements,
  LookalikeEditableElements,
  RemovableElements,
} from '../../drawer/components/SegmentListItem/useSegmentHeaderElements.tsx';
import { createSegmentMetadata } from '../../drawer/components/SegmentsList/utils/createSegmentMetadata.ts';
import { formatReachValue } from '../../drawer/components/SegmentsList/utils/formatReachValue.ts';
import { createBasicInfoPropsFromLookalike } from '../../utils/createBasicInfoProps.ts';
import { headerRowStyles } from '../../utils/segmentListItemStyles.ts';

export type SeedSegmentItemProps = {
  segment: LookalikeFormSegment;
  headerConfig?: ExpandableElements | RemovableElements | LookalikeEditableElements;
  isSelectedItem?: boolean;
};

export const SeedSegmentItem = ({ segment, headerConfig, isSelectedItem = false }: SeedSegmentItemProps) => {
  const { t } = useTranslation();
  const { organizations, providers } = useReferenceData();

  const seedBasicInfoProps = useMemo(() => {
    const seedMetadata = createSegmentMetadata(segment, t, segment.account_id || '');
    const seedDisplayReach = formatReachValue(t, seedMetadata.reach);
    const seedOrganizationName = segment.account_id ? organizations[segment.account_id] : t('N / A');
    const seedProviderName = segment.provider_id ? providers[segment.provider_id] || undefined : undefined;

    return createBasicInfoPropsFromLookalike({
      id: seedMetadata.id,
      reach: seedMetadata.reach,
      displayReach: seedDisplayReach,
      category: seedMetadata.category || '',
      organizationName: seedOrganizationName,
      providerName: seedProviderName,
      isSelectedList: true,
      lookalikeReachRange: seedMetadata.lookalikeReachRange,
      isSeed: true,
    });
  }, [segment, t, organizations, providers]);

  const content = useMemo(() => {
    return headerConfig ? (
      <SegmentHeader
        name={t('Seed Segment')}
        isLAL={true}
        config={headerConfig}
        isSelectedItem={isSelectedItem}
        isSeedAggregate={true}
      />
    ) : (
      <Box sx={headerRowStyles}>
        <Typography
          variant="body1Bold"
          component="span"
          sx={{
            flexGrow: 1,
            ml: 1.5,
          }}
        >
          {t('Seed Segment')}
        </Typography>
      </Box>
    );
  }, [t, headerConfig, isSelectedItem]);

  return (
    <IndentedSections sx={{ ml: 4 }} dataTest="seed-aggregate-indent">
      {content}
      <BasicInfo {...seedBasicInfoProps} />
    </IndentedSections>
  );
};
